/**
 * Modal — clinical-ops sends a request to the patient's prescriber (their
 * assigned prescriber, else the default; the modal names them), or edits an
 * existing Write-Rx task.
 *
 * Create mode offers three request types:
 *   - Re-script order (default): clones a fulfilled/cancelled order into a
 *     $0 awaiting order for the chosen product. That order flows through the
 *     clinician worklist like any purchase, so it lands on the Ready list and
 *     the prescriber can prescribe from it.
 *   - Verify intake & return to Ready: audited ops override on an awaiting
 *     order whose legacy intake gate is blocking it.
 *   - Healthie task only: the original template/textarea flow. A bare task
 *     never reaches the worklist, so it now carries a warning.
 *
 * Edit mode (pass `existingTask`):
 *   - Just the textarea, pre-filled with the current content. Submit
 *     calls PATCH instead of POST. Template/medication dropdowns are
 *     hidden — edits are about tweaking the body, not re-templating.
 *
 * Endpoints (all under /admin/clinical-admin/patients/{id}/prescriptions):
 *   GET  /assignee, GET /rescript-targets, POST /rescript-orders,
 *   POST /orders/{orderId}/verify-intake, POST|PATCH /tasks(/{taskId}).
 * Every mutation invalidates the prescriptions query so the cards refresh.
 */
import { useEffect, useMemo, useState } from 'react';

import {
  useCreateClinicalRxTask,
  useCreateRescriptOrder,
  useRescriptTargets,
  useRxTaskAssignee,
  useUpdateClinicalRxTask,
  useVerifyIntakeOverride,
} from '@/lib/api/clinicalAdminHooks';
import type {
  OrderableMedication,
  RescriptAwaitingOrder,
  RescriptOrderResult,
  RescriptSourceOrder,
  VerifyIntakeResult,
} from '@/types/clinicalAdmin';

import { fmtDate, Toast } from './ui';

export interface RxTaskBaseContext {
  firstName: string | null;
  state: string | null;
}

export interface RxTaskMedicationContext {
  drugName: string;
  partnerLabel: string;
  medicationTemplateName: string | null;
}

interface Template {
  id: string;
  label: string;
  // Builder receives the base patient context plus an optional resolved
  // medication. When medication is provided the template should mirror
  // the auto-task format (Write Rx — DRUG — via CHANNEL). When absent it
  // falls back to a generic body so ops can still pick a template
  // without picking a drug first.
  build: (
    base: RxTaskBaseContext,
    med: RxTaskMedicationContext | null,
  ) => string;
}

function joinLines(lines: (string | null | false)[]): string {
  return lines.filter((l): l is string => Boolean(l)).join('\n');
}

const TEMPLATES: Template[] = [
  {
    id: 'script_rewrite',
    label: 'Script re-write',
    build: (base, med) =>
      med
        ? joinLines([
            `Please re-write the script for ${base.firstName ?? 'patient'} — ${med.drugName} — via ${med.partnerLabel}.`,
            med.medicationTemplateName
              ? `Use medication template: "${med.medicationTemplateName}"`
              : null,
            base.state ? `State: ${base.state}.` : null,
          ])
        : joinLines([
            `Please re-write the script for ${base.firstName ?? 'patient'}.`,
            base.state ? `State: ${base.state}.` : null,
          ]),
  },
  {
    id: 'adjust_dose',
    label: 'Adjust dose',
    build: (_base, med) =>
      med
        ? joinLines([
            `Patient requested dose adjustment for ${med.drugName} — via ${med.partnerLabel}.`,
            'Current dose: (clinical ops to fill in)',
            'Requested change: (clinical ops to fill in)',
            med.medicationTemplateName
              ? `Use medication template: "${med.medicationTemplateName}"`
              : null,
            'Please review and update.',
          ])
        : joinLines([
            'Patient requested dose adjustment.',
            'Current dose: (clinical ops to fill in)',
            'Requested change: (clinical ops to fill in)',
            'Please review and update.',
          ]),
  },
  {
    id: 'cancel_script',
    label: 'Cancel script',
    build: (base, med) =>
      med
        ? joinLines([
            `Please cancel active ${med.drugName} script for ${base.firstName ?? 'patient'} — via ${med.partnerLabel}.`,
            'Reason: (clinical ops to fill in)',
          ])
        : joinLines([
            `Please cancel active script for ${base.firstName ?? 'patient'}.`,
            'Reason: (clinical ops to fill in)',
          ]),
  },
  {
    id: 'custom',
    label: 'Custom (blank)',
    build: () => '',
  },
];

// Stable key for dropdown options — a patient COULD have the same product
// id routed via two partners in rare cases, so we don't dedupe on
// product_id alone.
function medOptionKey(m: OrderableMedication): string {
  return `${m.product_id}::${m.partner_key}`;
}

function medOptionLabel(m: OrderableMedication): string {
  const name = m.medication_template_name ?? m.product_id;
  return `${name} — ${m.partner_label}`;
}

type RequestMode = 'rescript' | 'verify' | 'task';

const MODES: { value: RequestMode; label: string }[] = [
  { value: 'rescript', label: 'Re-script order' },
  { value: 'verify', label: 'Verify intake & return to Ready' },
  { value: 'task', label: 'Healthie task only' },
];

const REASON_MIN = 5;
const REASON_MAX = 500;
const NOTE_MAX = 500;

// Worklist stage codes → the labels the clinician sees on the worklist tabs.
const STAGE_LABELS: Record<string, string> = {
  ready_to_prescribe: 'Ready',
  awaiting_id: 'Missing ID',
  approval_required: 'Approval',
  sync_required: 'Sync required',
  visit_scheduled: 'Visit scheduled',
  intake_required: 'Check-in / intake missing',
  prescribed: 'Approved / prescribed',
  rejected: 'Rejected',
  missing_info: 'Missing info',
};

export function stageLabel(stage: string | null | undefined): string {
  if (!stage) return '—';
  return STAGE_LABELS[stage] ?? stage;
}

function sourceOptionLabel(source: RescriptSourceOrder): string {
  return [
    source.order_number,
    source.product_name,
    source.partner_label,
    source.status,
    fmtDate(source.created_at),
  ].join(' · ');
}

function awaitingOptionLabel(order: RescriptAwaitingOrder): string {
  return [
    order.order_number,
    order.product_name,
    stageLabel(order.stage),
    fmtDate(order.created_at),
  ].join(' · ');
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message.slice(0, 300) : fallback;
}

/** Human line for the re-script result panel, keyed by fulfillment outcome. */
export function rescriptOutcomeLine(
  result: RescriptOrderResult,
  prescriber = 'the prescriber',
): string {
  const number = result.order_number;
  switch (result.fulfillment.outcome) {
    case 'task_fired':
      return `On the Ready list for ${prescriber} as ${number}.`;
    case 'submitted':
      return `A matching script was already on file, so ${number} was submitted to the pharmacy directly.`;
    case 'parked': {
      const detail = result.fulfillment.detail;
      const reason =
        typeof detail === 'string' && detail.trim()
          ? detail
          : 'blocked by a clinical gate';
      return `Created ${number} but parked: ${reason}. It will show in the matching worklist tab.`;
    }
  }
}

const INTAKE_REGATED_LINE =
  'The new product needs its own intake; the patient must complete it before it reaches Ready.';

function taskOnlyWarning(prescriber: string): string {
  return `This creates a chart task only. It will not appear on the Ready list for ${prescriber}, and they can't prescribe from it. Use Re-script order unless you only need a note on the chart.`;
}

const LABEL_CLASS = 'font-mono uppercase text-12 text-ink-muted';
const SELECT_CLASS =
  'h-36 px-10 bg-white border border-line text-13 rounded focus:outline-none focus:border-ink';
const TEXTAREA_CLASS =
  'px-10 py-8 bg-white border border-line text-13 rounded focus:outline-none focus:border-ink resize-y';
const PRIMARY_BTN =
  'h-36 px-15 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed';
const SECONDARY_BTN =
  'h-36 px-15 bg-sunken text-ink type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed';

export function CreateRxTaskModal({
  patientId,
  baseContext,
  orderableMedications,
  existingTask,
  onClose,
}: {
  patientId: string;
  baseContext: RxTaskBaseContext;
  orderableMedications: OrderableMedication[];
  /** When present → edit mode. */
  existingTask?: { id: string; content: string } | null;
  onClose: () => void;
}) {
  const isEdit = !!existingTask;
  const create = useCreateClinicalRxTask(patientId);
  const update = useUpdateClinicalRxTask(patientId);
  const targets = useRescriptTargets(patientId, !isEdit);
  const rescript = useCreateRescriptOrder(patientId);
  const verify = useVerifyIntakeOverride(patientId);
  const assignee = useRxTaskAssignee(patientId);
  const prescriberName = assignee.data?.name ?? 'the prescriber';
  const isPending =
    (isEdit ? update.isPending : create.isPending) ||
    rescript.isPending ||
    verify.isPending;

  const [mode, setMode] = useState<RequestMode>('rescript');

  // ---- Re-script order -----------------------------------------------------
  const sources = useMemo(() => targets.data?.sources ?? [], [targets.data]);
  const targetProducts = useMemo(
    () => targets.data?.targets ?? [],
    [targets.data],
  );
  const awaiting = useMemo(() => targets.data?.awaiting ?? [], [targets.data]);
  const [sourceOrderId, setSourceOrderId] = useState<string>('');
  const [targetProductId, setTargetProductId] = useState<string>('');
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [rescriptReason, setRescriptReason] = useState('');
  const [rescriptNote, setRescriptNote] = useState('');
  const [rescriptError, setRescriptError] = useState<string | null>(null);
  const [rescriptResult, setRescriptResult] =
    useState<RescriptOrderResult | null>(null);

  const selectedSource = useMemo(
    () => sources.find((s) => s.order_id === sourceOrderId) ?? null,
    [sources, sourceOrderId],
  );
  const visibleTargets = useMemo(
    () =>
      showAllProducts
        ? targetProducts
        : targetProducts.filter((t) => t.same_family),
    [targetProducts, showAllProducts],
  );

  // Default the source to the most recent order and the target to that
  // order's own product whenever the list (or the source) changes and the
  // current selection is no longer valid.
  useEffect(() => {
    if (!sources.length) return;
    if (!sources.some((s) => s.order_id === sourceOrderId)) {
      setSourceOrderId(sources[0].order_id);
    }
  }, [sources, sourceOrderId]);
  useEffect(() => {
    if (!visibleTargets.length) return;
    if (visibleTargets.some((t) => t.product_id === targetProductId)) return;
    const own = selectedSource?.product_id;
    const preferred =
      own && visibleTargets.some((t) => t.product_id === own)
        ? own
        : visibleTargets[0].product_id;
    setTargetProductId(preferred);
  }, [visibleTargets, selectedSource, targetProductId]);

  const rescriptReasonTrimmed = rescriptReason.trim();
  const rescriptNoteTrimmed = rescriptNote.trim();
  const canSubmitRescript =
    !isPending &&
    Boolean(sourceOrderId) &&
    Boolean(targetProductId) &&
    rescriptReasonTrimmed.length >= REASON_MIN &&
    rescriptReasonTrimmed.length <= REASON_MAX &&
    rescriptNoteTrimmed.length <= NOTE_MAX;

  const onSubmitRescript = async () => {
    if (!canSubmitRescript) return;
    setRescriptError(null);
    try {
      const result = await rescript.mutateAsync({
        source_order_id: sourceOrderId,
        target_product_id: targetProductId,
        reason: rescriptReasonTrimmed,
        ...(rescriptNoteTrimmed ? { note: rescriptNoteTrimmed } : {}),
      });
      setRescriptResult(result);
    } catch (e) {
      setRescriptError(
        errorMessage(e, 'Could not create the re-script order. Please try again.'),
      );
    }
  };

  // ---- Verify intake -------------------------------------------------------
  const [awaitingOrderId, setAwaitingOrderId] = useState<string>('');
  const [verifyReason, setVerifyReason] = useState('');
  const [verifyEvidence, setVerifyEvidence] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<VerifyIntakeResult | null>(
    null,
  );
  useEffect(() => {
    if (!awaiting.length) return;
    const current = awaiting.find((o) => o.order_id === awaitingOrderId);
    if (current?.overridable) return;
    const firstOverridable = awaiting.find((o) => o.overridable);
    setAwaitingOrderId(firstOverridable?.order_id ?? '');
  }, [awaiting, awaitingOrderId]);
  const selectedAwaiting = useMemo(
    () => awaiting.find((o) => o.order_id === awaitingOrderId) ?? null,
    [awaiting, awaitingOrderId],
  );
  const blockedAwaiting = useMemo(
    () => awaiting.filter((o) => !o.overridable),
    [awaiting],
  );
  const verifyReasonTrimmed = verifyReason.trim();
  const verifyEvidenceTrimmed = verifyEvidence.trim();
  const canSubmitVerify =
    !isPending &&
    Boolean(selectedAwaiting?.overridable) &&
    verifyReasonTrimmed.length >= REASON_MIN &&
    verifyReasonTrimmed.length <= REASON_MAX &&
    verifyEvidenceTrimmed.length >= REASON_MIN &&
    verifyEvidenceTrimmed.length <= REASON_MAX;

  const onSubmitVerify = async () => {
    if (!canSubmitVerify || !selectedAwaiting) return;
    setVerifyError(null);
    try {
      const result = await verify.mutateAsync({
        orderId: selectedAwaiting.order_id,
        reason: verifyReasonTrimmed,
        evidence: verifyEvidenceTrimmed,
      });
      setVerifyResult(result);
    } catch (e) {
      setVerifyError(
        errorMessage(e, 'Could not verify intake. Please try again.'),
      );
    }
  };

  // ---- Healthie task (legacy) ----------------------------------------------
  const [selectedMedKey, setSelectedMedKey] = useState<string>('');
  const selectedMed = useMemo<OrderableMedication | null>(
    () =>
      orderableMedications.find((m) => medOptionKey(m) === selectedMedKey) ??
      null,
    [orderableMedications, selectedMedKey],
  );

  const [templateId, setTemplateId] = useState<string>(TEMPLATES[0].id);

  const medContext = useMemo<RxTaskMedicationContext | null>(() => {
    if (!selectedMed) return null;
    const drugName =
      selectedMed.medication_template_name?.split(/[—-]/)[0].trim() ||
      selectedMed.medication_template_name ||
      selectedMed.product_id;
    return {
      drugName,
      partnerLabel: selectedMed.partner_label,
      medicationTemplateName: selectedMed.medication_template_name,
    };
  }, [selectedMed]);

  const [content, setContent] = useState<string>(() => {
    if (isEdit) return existingTask?.content ?? '';
    return TEMPLATES[0].build(baseContext, null);
  });
  // Track manual edits so we warn before clobbering with a template
  // re-render. Reset whenever a template/medication re-render fills the
  // textarea — that's "fresh from template", not a manual edit.
  const [touched, setTouched] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    tone: 'good' | 'danger';
  } | null>(null);

  // Close on Esc — both modes.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, isPending]);

  // Helper: re-render the textarea from the active template+med pair,
  // confirming first if the user has unsaved edits. Used by both
  // medication-change and template-change handlers so the prompt only
  // lives in one place.
  const tryReRender = (
    nextTemplateId: string,
    nextMed: RxTaskMedicationContext | null,
  ): boolean => {
    if (touched && content.trim().length > 0) {
      const ok = window.confirm(
        'You have unsaved edits. Switching will overwrite them. Continue?',
      );
      if (!ok) return false;
    }
    const tpl = TEMPLATES.find((t) => t.id === nextTemplateId);
    if (!tpl) return false;
    setContent(tpl.build(baseContext, nextMed));
    setTouched(false);
    return true;
  };

  const onMedChange = (nextKey: string) => {
    if (isEdit) return;
    const nextMed =
      orderableMedications.find((m) => medOptionKey(m) === nextKey) ?? null;
    const nextContext: RxTaskMedicationContext | null = nextMed
      ? {
          drugName:
            nextMed.medication_template_name?.split(/[—-]/)[0].trim() ||
            nextMed.medication_template_name ||
            nextMed.product_id,
          partnerLabel: nextMed.partner_label,
          medicationTemplateName: nextMed.medication_template_name,
        }
      : null;
    if (tryReRender(templateId, nextContext)) {
      setSelectedMedKey(nextKey);
    }
  };

  const onTemplateChange = (nextId: string) => {
    if (isEdit) return;
    if (tryReRender(nextId, medContext)) {
      setTemplateId(nextId);
    }
  };

  const trimmed = content.trim();
  const dirty = isEdit ? trimmed !== (existingTask?.content ?? '').trim() : true;
  const canSubmitTask = trimmed.length > 0 && !isPending && dirty;

  const onSubmitTask = async () => {
    if (!canSubmitTask) return;
    try {
      if (isEdit && existingTask) {
        await update.mutateAsync({ taskId: existingTask.id, content: trimmed });
        setToast({ message: 'Task updated.', tone: 'good' });
      } else {
        await create.mutateAsync({ content: trimmed, template_id: templateId });
        setToast({ message: 'Task created for the prescriber.', tone: 'good' });
      }
      setTimeout(onClose, 600);
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : 'Could not save task. Please try again.';
      setToast({ message: msg.slice(0, 200), tone: 'danger' });
    }
  };

  const showTaskForm = isEdit || mode === 'task';
  const heading = isEdit ? 'Edit Write-Rx task' : 'Clinician request';
  const subheading = isEdit
    ? 'Updates the task body in the prescriber’s Healthie queue.'
    : mode === 'rescript'
      ? 'Creates a $0 awaiting order that lands on the prescriber’s worklist.'
      : mode === 'verify'
        ? 'Audited override: returns a gated awaiting order to the Ready list.'
        : 'Assigned to the patient’s prescriber. Deep-linked to this patient’s chart.';
  const submitLabel = isEdit ? 'Save changes' : 'Create task';
  const submitPendingLabel = isEdit ? 'Saving…' : 'Creating…';

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-start justify-center p-24 bg-ink/60 backdrop-blur-sm overflow-y-auto"
        onClick={() => {
          if (!isPending) onClose();
        }}
      >
        <div
          className="bg-white border border-default shadow-xl w-full max-w-content-md flex flex-col gap-16 p-15 my-24"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-12">
            <div className="flex flex-col gap-4">
              <h2 className="type-heading-lg">{heading}</h2>
              <p className="type-body-sm text-ink-muted">{subheading}</p>
              {assignee.data && (
                <p className="type-body-sm">
                  <span className="text-ink-muted">Goes to </span>
                  {assignee.data.name}
                  <span className="text-ink-muted">
                    {assignee.data.assigned
                      ? ' (assigned prescriber)'
                      : ' (default prescriber)'}
                  </span>
                </p>
              )}
            </div>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={onClose}
              aria-label="Close"
              disabled={isPending}
            >
              Close
            </button>
          </div>

          {!isEdit && (
            <div
              className="flex flex-wrap gap-6"
              role="group"
              aria-label="Request type"
            >
              {MODES.map((entry) => (
                <button
                  key={entry.value}
                  type="button"
                  aria-pressed={mode === entry.value}
                  disabled={isPending}
                  onClick={() => setMode(entry.value)}
                  className={`min-h-32 px-12 rounded type-label-sm border disabled:opacity-40 ${
                    mode === entry.value
                      ? 'bg-ink text-page border-ink'
                      : 'border-line text-ink-muted hover:bg-sunken'
                  }`}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          )}

          {!isEdit && mode === 'rescript' && (
            <RescriptSection
              loading={targets.isLoading}
              loadFailed={targets.isError}
              sources={sources}
              visibleTargets={visibleTargets}
              hiddenTargetCount={targetProducts.length - visibleTargets.length}
              sourceOrderId={sourceOrderId}
              onSourceChange={setSourceOrderId}
              targetProductId={targetProductId}
              onTargetChange={setTargetProductId}
              showAllProducts={showAllProducts}
              onShowAllChange={setShowAllProducts}
              reason={rescriptReason}
              onReasonChange={setRescriptReason}
              note={rescriptNote}
              onNoteChange={setRescriptNote}
              error={rescriptError}
              result={rescriptResult}
              prescriber={prescriberName}
              pending={isPending}
              canSubmit={canSubmitRescript}
              onSubmit={onSubmitRescript}
              onCancel={onClose}
            />
          )}

          {!isEdit && mode === 'verify' && (
            <VerifySection
              loading={targets.isLoading}
              loadFailed={targets.isError}
              awaiting={awaiting}
              blocked={blockedAwaiting}
              awaitingOrderId={awaitingOrderId}
              onAwaitingChange={setAwaitingOrderId}
              reason={verifyReason}
              onReasonChange={setVerifyReason}
              evidence={verifyEvidence}
              onEvidenceChange={setVerifyEvidence}
              error={verifyError}
              result={verifyResult}
              pending={isPending}
              canSubmit={canSubmitVerify}
              onSubmit={onSubmitVerify}
              onCancel={onClose}
            />
          )}

          {showTaskForm && (
            <>
              {!isEdit && (
                <div
                  role="alert"
                  className="border border-warning/40 bg-warning-wash text-ink px-12 py-10 rounded type-body-sm"
                >
                  {taskOnlyWarning(prescriberName)}
                </div>
              )}

              {!isEdit && (
                <>
                  <label className="flex flex-col gap-4">
                    <span className={LABEL_CLASS}>
                      Target medication (optional)
                    </span>
                    <select
                      value={selectedMedKey}
                      onChange={(e) => onMedChange(e.target.value)}
                      disabled={isPending}
                      className={SELECT_CLASS}
                    >
                      <option value="">(none — write generally)</option>
                      {orderableMedications.map((m) => (
                        <option key={medOptionKey(m)} value={medOptionKey(m)}>
                          {medOptionLabel(m)}
                        </option>
                      ))}
                    </select>
                    <span className="type-body-xs text-ink-muted">
                      Picks the pharmacy + medication template so the task
                      body reads the way prescribers expect (mirrors the auto-task
                      format). Leave blank if the task isn’t about a specific
                      drug.
                    </span>
                  </label>

                  <label className="flex flex-col gap-4">
                    <span className={LABEL_CLASS}>Template</span>
                    <select
                      value={templateId}
                      onChange={(e) => onTemplateChange(e.target.value)}
                      disabled={isPending}
                      className={SELECT_CLASS}
                    >
                      {TEMPLATES.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              <label className="flex flex-col gap-4">
                <span className={LABEL_CLASS}>Task body</span>
                <textarea
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                    setTouched(true);
                  }}
                  disabled={isPending}
                  rows={8}
                  placeholder="What does the prescriber need to do?"
                  className={`${TEXTAREA_CLASS} min-h-[10rem]`}
                />
                <span className="type-body-xs text-ink-muted">
                  The prescriber sees this verbatim. {trimmed.length}/2000 characters.
                </span>
              </label>

              <div className="flex items-center justify-end gap-10">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isPending}
                  className={SECONDARY_BTN}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSubmitTask}
                  disabled={!canSubmitTask}
                  className={PRIMARY_BTN}
                >
                  {isPending ? submitPendingLabel : submitLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onDone={() => setToast(null)}
        />
      )}
    </>
  );
}

// --------------------------------------------------------------------------
// Re-script order section
// --------------------------------------------------------------------------

function RescriptSection({
  loading,
  loadFailed,
  sources,
  visibleTargets,
  hiddenTargetCount,
  sourceOrderId,
  onSourceChange,
  targetProductId,
  onTargetChange,
  showAllProducts,
  onShowAllChange,
  reason,
  onReasonChange,
  note,
  onNoteChange,
  error,
  result,
  prescriber,
  pending,
  canSubmit,
  onSubmit,
  onCancel,
}: {
  loading: boolean;
  loadFailed: boolean;
  sources: RescriptSourceOrder[];
  visibleTargets: { product_id: string; product_name: string }[];
  hiddenTargetCount: number;
  sourceOrderId: string;
  onSourceChange: (v: string) => void;
  targetProductId: string;
  onTargetChange: (v: string) => void;
  showAllProducts: boolean;
  onShowAllChange: (v: boolean) => void;
  reason: string;
  onReasonChange: (v: string) => void;
  note: string;
  onNoteChange: (v: string) => void;
  error: string | null;
  result: RescriptOrderResult | null;
  prescriber: string;
  pending: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  if (result) {
    return (
      <ResultPanel
        title={`Re-script order ${result.order_number} created`}
        lines={[
          rescriptOutcomeLine(result, prescriber),
          ...(result.intake_regated ? [INTAKE_REGATED_LINE] : []),
        ]}
        onDone={onCancel}
      />
    );
  }
  if (loading) {
    return (
      <p className="type-body-sm text-ink-muted">Loading orders…</p>
    );
  }
  if (loadFailed) {
    return (
      <p role="alert" className="type-body-sm text-danger">
        Could not load this patient’s orders. Close and try again.
      </p>
    );
  }
  if (!sources.length) {
    return (
      <p className="type-body-sm text-ink-muted">
        No fulfilled or cancelled order to clone from. A re-script needs a
        previous pharmacy order; use Healthie task only if this is a first
        prescription.
      </p>
    );
  }
  return (
    <>
      <label className="flex flex-col gap-4">
        <span className={LABEL_CLASS}>Source order</span>
        <select
          value={sourceOrderId}
          onChange={(e) => onSourceChange(e.target.value)}
          disabled={pending}
          className={SELECT_CLASS}
        >
          {sources.map((s) => (
            <option key={s.order_id} value={s.order_id}>
              {sourceOptionLabel(s)}
            </option>
          ))}
        </select>
        <span className="type-body-xs text-ink-muted">
          The fill being replaced. Its identity, address and pharmacy routing
          seed the new order; nothing about it is changed.
        </span>
      </label>

      <label className="flex flex-col gap-4">
        <span className={LABEL_CLASS}>New product</span>
        <select
          value={targetProductId}
          onChange={(e) => onTargetChange(e.target.value)}
          disabled={pending || !visibleTargets.length}
          className={SELECT_CLASS}
        >
          {visibleTargets.map((t) => (
            <option key={t.product_id} value={t.product_id}>
              {t.product_name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-6 type-body-xs text-ink-muted">
          <input
            type="checkbox"
            checked={showAllProducts}
            disabled={pending}
            onChange={(e) => onShowAllChange(e.target.checked)}
          />
          Show all products
          {!showAllProducts && hiddenTargetCount > 0
            ? ` (${hiddenTargetCount} outside this treatment family)`
            : ''}
        </label>
      </label>

      <label className="flex flex-col gap-4">
        <span className={LABEL_CLASS}>Reason</span>
        <textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          disabled={pending}
          rows={3}
          maxLength={REASON_MAX}
          placeholder="Why does this patient need a new script?"
          className={TEXTAREA_CLASS}
        />
        <span className="type-body-xs text-ink-muted">
          Recorded on the order’s audit log. {reason.trim().length}/
          {REASON_MAX} characters, at least {REASON_MIN}.
        </span>
      </label>

      <label className="flex flex-col gap-4">
        <span className={LABEL_CLASS}>Note for pharmacy/clinician (optional)</span>
        <textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          disabled={pending}
          rows={2}
          maxLength={NOTE_MAX}
          className={TEXTAREA_CLASS}
        />
      </label>

      {error && (
        <p role="alert" className="type-body-sm text-danger">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-10">
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className={SECONDARY_BTN}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className={PRIMARY_BTN}
        >
          {pending ? 'Creating…' : 'Create re-script order'}
        </button>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Verify intake section
// --------------------------------------------------------------------------

function VerifySection({
  loading,
  loadFailed,
  awaiting,
  blocked,
  awaitingOrderId,
  onAwaitingChange,
  reason,
  onReasonChange,
  evidence,
  onEvidenceChange,
  error,
  result,
  pending,
  canSubmit,
  onSubmit,
  onCancel,
}: {
  loading: boolean;
  loadFailed: boolean;
  awaiting: RescriptAwaitingOrder[];
  blocked: RescriptAwaitingOrder[];
  awaitingOrderId: string;
  onAwaitingChange: (v: string) => void;
  reason: string;
  onReasonChange: (v: string) => void;
  evidence: string;
  onEvidenceChange: (v: string) => void;
  error: string | null;
  result: VerifyIntakeResult | null;
  pending: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  if (result) {
    return (
      <ResultPanel
        title={`${result.order_number} returned to the clinician`}
        lines={[
          `${result.order_number} moved from ${stageLabel(result.stage_before)} to ${stageLabel(result.stage_after)}.`,
          ...(result.task_ids.length === 0 && result.fire_reason
            ? [`No Write-Rx task was created: ${result.fire_reason}.`]
            : []),
        ]}
        onDone={onCancel}
      />
    );
  }
  if (loading) {
    return (
      <p className="type-body-sm text-ink-muted">Loading awaiting orders…</p>
    );
  }
  if (loadFailed) {
    return (
      <p role="alert" className="type-body-sm text-danger">
        Could not load this patient’s orders. Close and try again.
      </p>
    );
  }
  if (!awaiting.length) {
    return (
      <p className="type-body-sm text-ink-muted">
        No open awaiting order. Nothing is waiting on intake for this patient.
      </p>
    );
  }
  return (
    <>
      <label className="flex flex-col gap-4">
        <span className={LABEL_CLASS}>Awaiting order</span>
        <select
          value={awaitingOrderId}
          onChange={(e) => onAwaitingChange(e.target.value)}
          disabled={pending}
          className={SELECT_CLASS}
        >
          {!awaiting.some((o) => o.overridable) && (
            <option value="">(no overridable order)</option>
          )}
          {awaiting.map((o) => (
            <option
              key={o.order_id}
              value={o.order_id}
              disabled={!o.overridable}
            >
              {awaitingOptionLabel(o)}
              {o.overridable ? '' : ' — not overridable'}
            </option>
          ))}
        </select>
        {blocked.length > 0 && (
          <ul className="type-body-xs text-ink-muted flex flex-col gap-2">
            {blocked.map((o) => (
              <li key={o.order_id}>
                {o.order_number}: {o.blocked_reason ?? 'not overridable'}
              </li>
            ))}
          </ul>
        )}
      </label>

      <label className="flex flex-col gap-4">
        <span className={LABEL_CLASS}>Reason</span>
        <textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          disabled={pending}
          rows={2}
          maxLength={REASON_MAX}
          placeholder="Why is this order being returned to the prescriber?"
          className={TEXTAREA_CLASS}
        />
      </label>

      <label className="flex flex-col gap-4">
        <span className={LABEL_CLASS}>What evidence did you verify?</span>
        <textarea
          value={evidence}
          onChange={(e) => onEvidenceChange(e.target.value)}
          disabled={pending}
          rows={3}
          maxLength={REASON_MAX}
          placeholder="e.g. Intake form completed in Healthie on 9/18, form id …"
          className={TEXTAREA_CLASS}
        />
        <span className="type-body-xs text-ink-muted">
          Both fields are recorded on the order as an audited override. At
          least {REASON_MIN} characters each.
        </span>
      </label>

      {error && (
        <p role="alert" className="type-body-sm text-danger">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-10">
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className={SECONDARY_BTN}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className={PRIMARY_BTN}
        >
          {pending ? 'Verifying…' : 'Verify & return to Ready'}
        </button>
      </div>
    </>
  );
}

function ResultPanel({
  title,
  lines,
  onDone,
}: {
  title: string;
  lines: string[];
  onDone: () => void;
}) {
  return (
    <div
      role="status"
      className="flex flex-col gap-10 border border-line bg-sunken rounded px-12 py-10"
    >
      <p className="type-label-md font-semibold">{title}</p>
      {lines.map((line) => (
        <p key={line} className="type-body-sm">
          {line}
        </p>
      ))}
      <div className="flex justify-end">
        <button type="button" onClick={onDone} className={PRIMARY_BTN}>
          Done
        </button>
      </div>
    </div>
  );
}
